import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { ListChangeSetCommentThreadsInput } from '@erp/rpc';
import { orpc } from '@/api/rpc/client';

/** Filters on a review's thread list; the review itself is the hook's argument. */
export type ChangeSetCommentThreadFilters = Omit<
  ListChangeSetCommentThreadsInput,
  'changeSetId'
>;

const commentProcedures = orpc.inventoryManagement.changeSetComments;

/**
 * A review's cell comment threads with their messages and anchors, oldest
 * first; polled so teammates' comments show up without a reload. Index them
 * by `anchor.cellKey` and look cells up with `gridCellCommentKeys`.
 */
export function useChangeSetCommentThreads(
  changeSetId: string | undefined,
  filters: ChangeSetCommentThreadFilters = {},
) {
  const query = useQuery({
    ...commentProcedures.listThreads.queryOptions({
      input: changeSetId ? { changeSetId, ...filters } : skipToken,
    }),
    refetchInterval: 30_000,
  });
  return { ...query, threads: query.data?.threads ?? [] };
}

/**
 * People a review's cell comments can @mention: active users who can read the
 * review. Shared by every composer on the page; it changes rarely.
 */
export function useChangeSetMentionableUsers(changeSetId: string | undefined) {
  return useQuery({
    ...commentProcedures.listMentionableUsers.queryOptions({
      input: changeSetId ? { changeSetId } : skipToken,
    }),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * The review a comment thread is on, for a link that names only the thread
 * (a mention notification's). Not found outside the organization.
 */
export function useLocateChangeSetCommentThread(threadId: string) {
  return useQuery({
    ...commentProcedures.locateThread.queryOptions({ input: { threadId } }),
    retry: false,
  });
}

/**
 * Every comment mutation refreshes all of the review's thread lists (any
 * filter), since a write can move a thread in or out of each of them — and
 * its Review-mode pages, where a thread starting or ending adds or drops a
 * line's thread, or a commented unchanged line.
 */
function useInvalidateThreads(changeSetId: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: commentProcedures.listThreads.key({ input: { changeSetId } }),
      }),
      queryClient.invalidateQueries({
        queryKey:
          orpc.inventoryManagement.metadataChangeSets.listReviewLines.key({
            input: { changeSetId },
          }),
      }),
    ]);
}

/** Opens a thread on a cell with its first message. */
export function useCreateChangeSetCommentThread(changeSetId: string) {
  const invalidate = useInvalidateThreads(changeSetId);
  return useMutation({
    ...commentProcedures.createThread.mutationOptions(),
    onSuccess: invalidate,
  });
}

export function useReplyToChangeSetCommentThread(changeSetId: string) {
  const invalidate = useInvalidateThreads(changeSetId);
  return useMutation({
    ...commentProcedures.reply.mutationOptions(),
    onSuccess: invalidate,
  });
}

/** Author only; the server refuses anyone else's message. */
export function useEditChangeSetCommentMessage(changeSetId: string) {
  const invalidate = useInvalidateThreads(changeSetId);
  return useMutation({
    ...commentProcedures.editMessage.mutationOptions(),
    onSuccess: invalidate,
  });
}

/** Author only. A thread whose last message is deleted no longer lists. */
export function useDeleteChangeSetCommentMessage(changeSetId: string) {
  const invalidate = useInvalidateThreads(changeSetId);
  return useMutation({
    ...commentProcedures.deleteMessage.mutationOptions(),
    onSuccess: invalidate,
  });
}

export function useResolveChangeSetCommentThread(changeSetId: string) {
  const invalidate = useInvalidateThreads(changeSetId);
  return useMutation({
    ...commentProcedures.resolveThread.mutationOptions(),
    onSuccess: invalidate,
  });
}

export function useReopenChangeSetCommentThread(changeSetId: string) {
  const invalidate = useInvalidateThreads(changeSetId);
  return useMutation({
    ...commentProcedures.reopenThread.mutationOptions(),
    onSuccess: invalidate,
  });
}
